import {
  EditOutlined,
  ExperimentOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Button, Divider, Form, InputNumber, Tooltip } from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { parseInputNumber } from 'app/utils/typing';

import styles from './RoomPrice.module.scss';

type RoomPriceProps = {
  isDrawerFullyOpen: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
};

export const RoomPrice = ({
  isDrawerFullyOpen,
  containerRef,
}: RoomPriceProps) => {
  const { currencyFormatter, currencySymbol } = useContext(CurrencyContext);

  const form = Form.useFormInstance<{ averageRoomPrice: number | null }>();
  const formRoomPrice = Form.useWatch('averageRoomPrice', form);

  const hasPrice = formRoomPrice !== null && formRoomPrice !== undefined;

  const [isEditing, setIsEditing] = useState(!hasPrice);
  const [isTooltipOpen, setIsTooltipOpen] = useState(!hasPrice);

  const inputRef = useRef<HTMLInputElement>(null);

  const showBlockingTooltip =
    isDrawerFullyOpen && isTooltipOpen && isEditing && !hasPrice;

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    if (showBlockingTooltip) {
      container.style.pointerEvents = 'none';
      container.style.overflow = 'hidden';
    } else {
      container.style.pointerEvents = '';
      container.style.overflow = '';
    }

    return () => {
      container.style.pointerEvents = '';
      container.style.overflow = '';
    };
  }, [showBlockingTooltip, containerRef]);

  return (
    <div className={styles.averageBar}>
      <ExperimentOutlined className={styles.icon} />{' '}
      <span>
        Average BAR RO NA/NR{' '}
        <Tooltip title="Average BAR RO NA/NR of the lowest room category, based on the current sales range for the next 8 weeks.">
          <InfoCircleOutlined className={styles.infoIcon} />
        </Tooltip>
      </span>
      {isEditing ? (
        <Tooltip
          align={{ offset: [0, 4] }}
          overlayClassName={styles.tooltip}
          open={isDrawerFullyOpen && isTooltipOpen && isEditing && !hasPrice}
          getPopupContainer={(node) => node}
          title={
            isEditing && !hasPrice ? (
              <div className={styles.tooltipContent}>
                <div>
                  <b>
                    Enter your average minimum BAR for your first room category.
                  </b>
                  <br />
                  <br />
                  <p>
                    This value is a reference for pricing estimation only and
                    will not be saved. Final prices depend on your actual
                    inventory rates.
                  </p>
                </div>
                <div className={styles.tooltipFooter}>
                  <Button
                    className={styles.tooltipButton}
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setIsTooltipOpen(false)}
                  >
                    Got it
                  </Button>
                </div>
              </div>
            ) : undefined
          }
        >
          <span>
            <Form.Item name="averageRoomPrice" noStyle>
              <InputNumber
                ref={inputRef}
                className={styles.averageRoomPriceInput}
                status={!hasPrice ? 'error' : undefined}
                autoFocus
                onBlur={() => {
                  if (hasPrice) {
                    setIsEditing(false);
                  }
                }}
                onPressEnter={() => {
                  if (hasPrice) {
                    setIsEditing(false);
                  }
                }}
                addonAfter={currencySymbol}
                min={0}
                parser={parseInputNumber}
              />
            </Form.Item>
          </span>
        </Tooltip>
      ) : (
        <>
          <div style={{ display: 'none' }}>
            <Form.Item name="averageRoomPrice" noStyle preserve>
              <InputNumber />
            </Form.Item>
          </div>
          <p className={styles.price} onClick={() => setIsEditing(true)}>
            {currencyFormatter(formRoomPrice ?? undefined)}
          </p>
        </>
      )}
      {!isEditing && (
        <EditOutlined
          className={styles.iconPointer}
          onClick={() => setIsEditing(true)}
        />
      )}
      <Divider type="vertical" />
      <p className={styles.info}>For estimation only, not saved.</p>
    </div>
  );
};
